import { useState } from "react";
import React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Pencil, Trash2, Package, Upload, ChevronDown, ChevronRight, KeyRound, Mail, Cpu, ShieldAlert, FileSignature } from "lucide-react";
import { RenewalQuotationDialog } from "@/components/RenewalQuotationDialog";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { PageHeader, EmptyState } from "@/components/Common";
import { fmtMoney } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ImportItemsDialog } from "@/components/ImportItemsDialog";

const empty = {
  name: "", specification: "", sku: "", unit: "pcs",
  stock_qty: 0, reorder_level: 0, price: 0, category: "", notes: "",
  type: "", brand: "", model: "", warranty_months: 0, support_months: 0,
  on_backorder: false,
  // Software-specific
  item_type: "hardware",
  vendor: "", price_monthly: 0, price_yearly: 0,
  license_type: "", license_key: "", registered_email: "",
  purchase_date: "", expiry_date: "",
};

const SOFTWARE_CATEGORIES = [
  "Office suite", "Antivirus", "Cloud", "OS", "Design",
  "Dev tools", "Backup", "Networking", "Other",
];

const STATUS_META = {
  ready: { label: "Ready stock", cls: "bg-emerald-50 text-emerald-800 ring-emerald-200" },
  backorder: { label: "Backorder", cls: "bg-amber-50 text-amber-800 ring-amber-200" },
  out_of_stock: { label: "Out of stock", cls: "bg-red-50 text-red-800 ring-red-200" },
};

/**
 * Turn a free-form multi-line/dash-separated spec string into a compact
 * title (first meaningful segment, capped) + an array of readable bullet
 * lines. Same split rules as the PDF renderer for consistency.
 */
function parseSpec(raw) {
  if (!raw || !String(raw).trim()) return { title: "", bullets: [] };
  const marker = "\u001e";
  let s = String(raw).trim().replace(/^[-•\s]+/, "");
  s = s.replace(/\r\n|\r|\n/g, marker);
  const bullets = s
    .split(" - ")
    .flatMap((p) => p.split(marker))
    .map((p) => p.trim().replace(/^[-•\s]+/, ""))
    .filter(Boolean);
  const first = bullets[0] || "";
  // If first bullet looks like "Key: Value" — use just the key as title.
  const title = first.includes(":")
    ? first.split(":")[0].trim() + (bullets.length > 1 ? " …" : "")
    : first.length > 60 ? first.slice(0, 60) + "…" : first;
  return { title, bullets };
}

export default function Items() {
  const { t } = useI18n();
  const qc = useQueryClient();
  const { data = [], isLoading } = useQuery({ queryKey: ["items"], queryFn: api.listItems });
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [confirmId, setConfirmId] = useState(null);
  const [search, setSearch] = useState("");
  const [importOpen, setImportOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all"); // all | ready | backorder | out_of_stock
  const [typeFilter, setTypeFilter] = useState("all"); // all | hardware | software
  const [expandedId, setExpandedId] = useState(null);
  const [renewalItem, setRenewalItem] = useState(null);

  const createMut = useMutation({
    mutationFn: (body) => api.createItem(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["items"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      setOpen(false); setForm(empty); toast.success("Item created");
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Error"),
  });
  const updateMut = useMutation({
    mutationFn: ({ id, body }) => api.updateItem(id, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["items"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      setOpen(false); setEditing(null); setForm(empty); toast.success("Item updated");
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Error"),
  });
  const deleteMut = useMutation({
    mutationFn: (id) => api.deleteItem(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["items"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      setConfirmId(null); toast.success("Deleted");
    },
  });

  function openCreate() { setEditing(null); setForm(empty); setOpen(true); }
  function openEdit(it) {
    setEditing(it);
    setForm({ ...empty, ...it });
    setOpen(true);
  }
  function submit(e) {
    e.preventDefault();
    if (!form.name.trim()) { toast.error(t("item_name") + " *"); return; }
    const isSw = form.item_type === "software";
    const body = {
      ...form,
      stock_qty: Number(form.stock_qty) || 0,
      reorder_level: Number(form.reorder_level) || 0,
      price: Number(form.price) || 0,
      price_monthly: Number(form.price_monthly) || 0,
      price_yearly: Number(form.price_yearly) || 0,
      warranty_months: Number(form.warranty_months) || 0,
      support_months: Number(form.support_months) || 0,
      on_backorder: !!form.on_backorder,
      // Force sensible defaults on software rows
      unit: isSw ? (form.unit || "license") : (form.unit || "pcs"),
    };
    if (editing) updateMut.mutate({ id: editing.id, body });
    else createMut.mutate(body);
  }

  const statusOf = (it) =>
    it.availability_status ||
    (Number(it.stock_qty) > 0 ? "ready" : (it.on_backorder ? "backorder" : "out_of_stock"));

  const counts = data.reduce(
    (acc, it) => {
      acc[statusOf(it)] = (acc[statusOf(it)] || 0) + 1;
      return acc;
    },
    { ready: 0, backorder: 0, out_of_stock: 0 }
  );

  const filtered = data.filter((it) => {
    if (typeFilter !== "all") {
      const t2 = it.item_type || "hardware";
      if (t2 !== typeFilter) return false;
    }
    if (statusFilter !== "all" && statusOf(it) !== statusFilter) return false;
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (
      (it.name || "").toLowerCase().includes(q) ||
      (it.specification || "").toLowerCase().includes(q) ||
      (it.sku || "").toLowerCase().includes(q) ||
      (it.category || "").toLowerCase().includes(q) ||
      (it.vendor || "").toLowerCase().includes(q)
    );
  });

  const typeCounts = data.reduce((acc, it) => {
    const t2 = it.item_type || "hardware";
    acc[t2] = (acc[t2] || 0) + 1;
    return acc;
  }, { hardware: 0, software: 0 });

  return (
    <div data-testid="items-page">
      <PageHeader
        overline={t("general_inventory")}
        title={t("general_inventory")}
        subtitle={t("general_inventory_hint")}
        actions={
          <>
            <Input
              data-testid="items-search"
              placeholder={t("search")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-64"
            />
            <Button data-testid="import-items-btn" variant="outline" onClick={() => setImportOpen(true)}>
              <Upload className="size-4 mr-1.5" /> Import CSV
            </Button>
            <Button data-testid="new-item-btn" onClick={openCreate} className="bg-primary hover:bg-primary/90">
              <Plus className="size-4 mr-1.5" /> {t("new_item")}
            </Button>
          </>
        }
      />

      <div className="flex flex-wrap items-center gap-2 mb-2" data-testid="items-type-toggle">
        {[
          { id: "all", label: "All items", icon: Package, count: data.length },
          { id: "hardware", label: "Hardware", icon: Cpu, count: typeCounts.hardware },
          { id: "software", label: "Software", icon: KeyRound, count: typeCounts.software },
        ].map((tab) => {
          const active = typeFilter === tab.id;
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              data-testid={`items-type-${tab.id}`}
              onClick={() => setTypeFilter(tab.id)}
              className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-md text-xs font-semibold ring-1 ring-inset transition ${
                active
                  ? "bg-primary text-white ring-primary"
                  : "bg-white text-slate-700 ring-slate-200 hover:ring-primary/40"
              }`}
            >
              <Icon className="size-3.5" />
              {tab.label}
              <span className={`ml-1 text-[10px] mono px-1.5 py-0.5 rounded-full ${active ? "bg-white/25" : "bg-slate-100 text-slate-600"}`}>
                {tab.count}
              </span>
            </button>
          );
        })}
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="items-tabs">
        {[
          { id: "all", label: "All", count: data.length, tone: "slate" },
          { id: "ready", label: "Ready stock", count: counts.ready, tone: "emerald" },
          { id: "backorder", label: "Backorder", count: counts.backorder, tone: "amber" },
          { id: "out_of_stock", label: "Out of stock", count: counts.out_of_stock, tone: "red" },
        ].map((tab) => {
          const active = statusFilter === tab.id;
          const toneMap = {
            slate: active ? "bg-slate-800 text-white ring-slate-800" : "bg-white text-slate-700 ring-slate-200 hover:bg-slate-50",
            emerald: active ? "bg-emerald-600 text-white ring-emerald-600" : "bg-white text-emerald-700 ring-emerald-200 hover:bg-emerald-50",
            amber: active ? "bg-amber-500 text-white ring-amber-500" : "bg-white text-amber-800 ring-amber-200 hover:bg-amber-50",
            red: active ? "bg-red-600 text-white ring-red-600" : "bg-white text-red-700 ring-red-200 hover:bg-red-50",
          };
          return (
            <button
              key={tab.id}
              data-testid={`items-tab-${tab.id}`}
              onClick={() => setStatusFilter(tab.id)}
              className={`inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-xs font-medium ring-1 ring-inset transition ${toneMap[tab.tone]}`}
            >
              {tab.label}
              <span className={`text-[10px] mono px-1.5 py-0.5 rounded-full ${active ? "bg-white/25" : "bg-slate-100 text-slate-600"}`}>
                {tab.count}
              </span>
            </button>
          );
        })}
      </div>

      {isLoading ? (
        <p className="text-muted-foreground">{t("loading")}</p>
      ) : filtered.length === 0 ? (
        <EmptyState
          title={t("no_data")}
          hint={t("new_item")}
          action={
            <Button data-testid="empty-new-item-btn" onClick={openCreate} className="bg-primary hover:bg-primary/90">
              <Plus className="size-4 mr-1.5" /> {t("new_item")}
            </Button>
          }
        />
      ) : (
        <div className="border border-border rounded-md bg-white overflow-x-auto" data-testid="items-table-wrap">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent border-b border-border">
                <TableHead className="overline">{t("item_name")}</TableHead>
                <TableHead className="overline">{t("specification")}</TableHead>
                <TableHead className="overline">{t("sku")}</TableHead>
                <TableHead className="overline">{t("unit")}</TableHead>
                <TableHead className="overline">Status</TableHead>
                <TableHead className="overline text-right">{t("stock_qty")}</TableHead>
                <TableHead className="overline text-right">{t("reorder_level")}</TableHead>
                <TableHead className="overline text-right">{t("price")}</TableHead>
                <TableHead className="overline text-right">{t("actions")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((it) => {
                const low = Number(it.stock_qty) <= Number(it.reorder_level);
                const status = statusOf(it);
                const meta = STATUS_META[status] || STATUS_META.out_of_stock;
                const spec = parseSpec(it.specification);
                const isExpanded = expandedId === it.id;
                const hasDetail = spec.bullets.length > 0;
                const isSw = (it.item_type || "hardware") === "software";
                let expBadge = null;
                if (isSw && it.expiry_date) {
                  const today = new Date(); today.setHours(0, 0, 0, 0);
                  const exp = new Date(it.expiry_date + "T00:00:00");
                  const days = Math.round((exp - today) / 86400000);
                  const style =
                    days < 0 ? "bg-red-100 text-red-800 ring-red-200"
                    : days <= 7 ? "bg-red-50 text-red-700 ring-red-200"
                    : days <= 14 ? "bg-amber-50 text-amber-800 ring-amber-200"
                    : days <= 30 ? "bg-amber-50 text-amber-700 ring-amber-200"
                    : "bg-emerald-50 text-emerald-700 ring-emerald-200";
                  expBadge = (
                    <span
                      data-testid={`sw-expiry-badge-${it.id}`}
                      className={`inline-flex items-center gap-1 text-[10px] font-mono px-1.5 py-0.5 rounded-md ring-1 ring-inset ${style}`}
                      title={`Expires ${it.expiry_date}`}
                    >
                      {days < 0 ? <ShieldAlert className="size-3" /> : null}
                      {days < 0 ? `expired ${-days}d` : `${days}d left`}
                    </span>
                  );
                }
                return (
                  <React.Fragment key={it.id}>
                  <TableRow data-testid={`item-row-${it.id}`}>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`size-7 grid place-items-center rounded-md ${isSw ? "bg-amber-100 text-amber-800" : "bg-primary/10 text-primary"}`}>
                          {isSw ? <KeyRound className="size-3.5" /> : <Package className="size-3.5" />}
                        </span>
                        <span>{it.name}</span>
                        {isSw && it.vendor && (
                          <span className="text-[10px] text-muted-foreground">· {it.vendor}</span>
                        )}
                        {expBadge}
                      </div>
                    </TableCell>
                    <TableCell className="text-xs max-w-[260px]">
                      {hasDetail ? (
                        <button
                          type="button"
                          data-testid={`spec-toggle-${it.id}`}
                          onClick={() => setExpandedId(isExpanded ? null : it.id)}
                          className="inline-flex items-center gap-1.5 text-left text-primary hover:underline"
                          title="Click to view full specification"
                        >
                          {isExpanded
                            ? <ChevronDown className="size-3.5 flex-none" />
                            : <ChevronRight className="size-3.5 flex-none" />}
                          <span className="truncate">{spec.title || "View details"}</span>
                          {spec.bullets.length > 1 && (
                            <span className="text-[10px] font-mono px-1 rounded-full bg-primary/10 text-primary">
                              +{spec.bullets.length - 1}
                            </span>
                          )}
                        </button>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell className="mono text-xs">{it.sku || "—"}</TableCell>
                    <TableCell className="text-muted-foreground text-xs">{it.unit}</TableCell>
                    <TableCell>
                      <span
                        data-testid={`item-status-${it.id}`}
                        className={`inline-flex text-[10px] font-semibold px-2 py-0.5 rounded-full ring-1 ring-inset ${meta.cls}`}
                      >
                        {meta.label}
                      </span>
                    </TableCell>
                    <TableCell className={`text-right mono ${low ? "text-red-600 font-semibold" : ""}`}>{it.stock_qty}</TableCell>
                    <TableCell className="text-right mono text-muted-foreground">{it.reorder_level}</TableCell>
                    <TableCell className="text-right mono">{fmtMoney(it.price)}</TableCell>
                    <TableCell className="text-right">
                      {isSw && (
                        <Button
                          data-testid={`renew-sw-${it.id}`}
                          variant="ghost"
                          size="sm"
                          onClick={() => setRenewalItem(it)}
                          className="text-emerald-700 hover:text-emerald-800"
                          title="Create renewal quotation"
                        >
                          <FileSignature className="size-3.5" />
                        </Button>
                      )}
                      <Button data-testid={`edit-item-${it.id}`} variant="ghost" size="sm" onClick={() => openEdit(it)}>
                        <Pencil className="size-3.5" />
                      </Button>
                      <Button data-testid={`delete-item-${it.id}`} variant="ghost" size="sm" onClick={() => setConfirmId(it.id)} className="text-red-600 hover:text-red-700">
                        <Trash2 className="size-3.5" />
                      </Button>
                    </TableCell>
                  </TableRow>
                  {isExpanded && hasDetail && (
                    <TableRow data-testid={`item-spec-detail-${it.id}`} className="hover:bg-transparent bg-primary/[0.02]">
                      <TableCell colSpan={9} className="py-3 px-6">
                        <div className="border-l-2 border-primary/40 pl-4 space-y-1 text-sm">
                          <p className="overline mb-1">Specification</p>
                          <ul className="space-y-0.5">
                            {spec.bullets.map((b, i) => {
                              const [k, ...rest] = b.split(":");
                              const val = rest.join(":").trim();
                              return (
                                <li key={i} className="text-slate-700">
                                  {val
                                    ? (<><span className="font-semibold text-slate-900">{k.trim()}:</span> {val}</>)
                                    : b}
                                </li>
                              );
                            })}
                          </ul>
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="item-form-dialog" className="sm:max-w-none max-w-none w-[min(96vw,1000px)] max-h-[92vh] flex flex-col p-0 gap-0">
          <DialogHeader className="px-6 pt-6 pb-3 border-b border-border shrink-0">
            <DialogTitle className="font-display">
              {editing ? t("edit_item") : t("new_item")}
            </DialogTitle>
            <DialogDescription className="text-xs text-muted-foreground">
              Register name, specification, stock, and price.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-4 px-6 py-4 overflow-y-auto flex-1 min-h-0">
            <div className="sm:col-span-2 flex items-center gap-2 p-2 bg-slate-50 rounded-md border border-border">
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold ml-1">Item type</span>
              <button
                type="button"
                data-testid="item-form-type-hardware"
                onClick={() => setForm({ ...form, item_type: "hardware", unit: form.unit === "license" ? "pcs" : form.unit })}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition ${
                  (form.item_type || "hardware") === "hardware"
                    ? "bg-primary text-white"
                    : "bg-white text-slate-700 ring-1 ring-inset ring-slate-200 hover:ring-primary/40"
                }`}
              >
                <Cpu className="size-3.5" /> Hardware
              </button>
              <button
                type="button"
                data-testid="item-form-type-software"
                onClick={() => setForm({ ...form, item_type: "software", unit: form.unit === "pcs" ? "license" : form.unit })}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition ${
                  form.item_type === "software"
                    ? "bg-primary text-white"
                    : "bg-white text-slate-700 ring-1 ring-inset ring-slate-200 hover:ring-primary/40"
                }`}
              >
                <KeyRound className="size-3.5" /> Software / License
              </button>
            </div>
            <div className="sm:col-span-2">
              <Label className="overline">{t("item_name")} *</Label>
              <Input data-testid="item-form-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={form.item_type === "software" ? "e.g. Microsoft 365 Business Standard" : "e.g. Dell Latitude 3520"} />
            </div>
            <div className="sm:col-span-2">
              <Label className="overline">{t("specification")}</Label>
              <Textarea data-testid="item-form-spec" value={form.specification} onChange={(e) => setForm({ ...form, specification: e.target.value })} rows={3} className="whitespace-pre-wrap break-words text-justify" />
            </div>
            <div>
              <Label className="overline">{t("sku")}</Label>
              <Input data-testid="item-form-sku" value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} />
            </div>
            <div>
              <Label className="overline">{t("category")}</Label>
              {form.item_type === "software" ? (
                <select
                  data-testid="item-form-category"
                  value={form.category}
                  onChange={(e) => setForm({ ...form, category: e.target.value })}
                  className="w-full h-10 rounded-md border border-input bg-white px-3 text-sm"
                >
                  <option value="">— Choose —</option>
                  {SOFTWARE_CATEGORIES.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              ) : (
                <Input data-testid="item-form-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} />
              )}
            </div>
            <div>
              <Label className="overline">{t("unit")}</Label>
              <Input data-testid="item-form-unit" value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} />
            </div>
            <div>
              <Label className="overline">{t("price")}</Label>
              <Input data-testid="item-form-price" type="number" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} />
            </div>
            <div>
              <Label className="overline">{t("stock_qty")}</Label>
              <Input data-testid="item-form-stock" type="number" step="1" value={form.stock_qty} onChange={(e) => setForm({ ...form, stock_qty: e.target.value })} />
            </div>
            <div>
              <Label className="overline">{t("reorder_level")}</Label>
              <Input data-testid="item-form-reorder" type="number" step="1" value={form.reorder_level} onChange={(e) => setForm({ ...form, reorder_level: e.target.value })} />
            </div>
            <div className="sm:col-span-2 flex items-start gap-2.5 rounded-md border border-amber-200 bg-amber-50 px-3 py-2">
              <input
                data-testid="item-form-backorder"
                id="on-backorder"
                type="checkbox"
                className="mt-0.5 size-4 accent-amber-500"
                checked={!!form.on_backorder}
                onChange={(e) => setForm({ ...form, on_backorder: e.target.checked })}
              />
              <label htmlFor="on-backorder" className="text-xs leading-snug cursor-pointer">
                <span className="font-semibold text-amber-900">Awaiting supplier (backorder)</span>
                <span className="block text-amber-800/70">Turn this ON if the item is out of stock but a Purchase Order has been placed with a supplier. Ignored when stock quantity is above 0.</span>
              </label>
            </div>
            <div>
              <Label className="overline">{t("asset_type")}</Label>
              <Input
                data-testid="item-form-type"
                placeholder="laptop · desktop · mobile · network …"
                value={form.type}
                onChange={(e) => setForm({ ...form, type: e.target.value })}
              />
            </div>
            <div>
              <Label className="overline">{t("brand")}</Label>
              <Input data-testid="item-form-brand" value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} />
            </div>
            <div>
              <Label className="overline">{t("model")}</Label>
              <Input data-testid="item-form-model" value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} />
            </div>
            <div>
              <Label className="overline">{t("warranty_months")}</Label>
              <Input data-testid="item-form-warranty" type="number" min="0" step="1" value={form.warranty_months} onChange={(e) => setForm({ ...form, warranty_months: e.target.value })} />
            </div>
            <div>
              <Label className="overline">{t("support_months")}</Label>
              <Input data-testid="item-form-support" type="number" min="0" step="1" value={form.support_months} onChange={(e) => setForm({ ...form, support_months: e.target.value })} />
            </div>

            {form.item_type === "software" && (
              <>
                <div className="sm:col-span-2 border-t border-dashed border-primary/30 pt-3 mt-1">
                  <p className="overline text-primary mb-2 flex items-center gap-2">
                    <KeyRound className="size-3.5" /> Software / license details
                  </p>
                </div>
                <div>
                  <Label className="overline">Vendor</Label>
                  <Input data-testid="sw-vendor" value={form.vendor} onChange={(e) => setForm({ ...form, vendor: e.target.value })}
                         placeholder="Microsoft · Adobe · Kaspersky …" />
                </div>
                <div>
                  <Label className="overline">Expiry / renewal date</Label>
                  <Input data-testid="sw-expiry" type="date" value={form.expiry_date}
                         onChange={(e) => setForm({ ...form, expiry_date: e.target.value })} />
                </div>
                <div>
                  <Label className="overline">Purchase date</Label>
                  <Input data-testid="sw-purchase" type="date" value={form.purchase_date}
                         onChange={(e) => setForm({ ...form, purchase_date: e.target.value })} />
                </div>
                <div>
                  <Label className="overline">Subscription base</Label>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="relative">
                      <span className="absolute left-2 top-1/2 -translate-y-1/2 text-[10px] text-muted-foreground uppercase">/mo</span>
                      <Input data-testid="sw-price-monthly" className="pl-9 mono" type="number" min="0" step="0.01"
                             value={form.price_monthly}
                             onChange={(e) => setForm({ ...form, price_monthly: e.target.value })} />
                    </div>
                    <div className="relative">
                      <span className="absolute left-2 top-1/2 -translate-y-1/2 text-[10px] text-muted-foreground uppercase">/yr</span>
                      <Input data-testid="sw-price-yearly" className="pl-9 mono" type="number" min="0" step="0.01"
                             value={form.price_yearly}
                             onChange={(e) => setForm({ ...form, price_yearly: e.target.value })} />
                    </div>
                  </div>
                </div>
                <div>
                  <Label className="overline">License type</Label>
                  <select
                    data-testid="sw-license-type"
                    value={form.license_type}
                    onChange={(e) => setForm({ ...form, license_type: e.target.value })}
                    className="w-full h-10 rounded-md border border-input bg-white px-3 text-sm"
                  >
                    <option value="">— Choose —</option>
                    <option value="key">Product key</option>
                    <option value="email">Registered to email</option>
                    <option value="both">Both (key + email)</option>
                  </select>
                </div>
                {(form.license_type === "key" || form.license_type === "both") && (
                  <div className="sm:col-span-2">
                    <Label className="overline">License key</Label>
                    <Input data-testid="sw-license-key" className="mono" value={form.license_key}
                           onChange={(e) => setForm({ ...form, license_key: e.target.value })}
                           placeholder="XXXXX-XXXXX-XXXXX-XXXXX-XXXXX" />
                  </div>
                )}
                {(form.license_type === "email" || form.license_type === "both") && (
                  <div className="sm:col-span-2">
                    <Label className="overline">Registered email</Label>
                    <Input data-testid="sw-registered-email" type="email" value={form.registered_email}
                           onChange={(e) => setForm({ ...form, registered_email: e.target.value })}
                           placeholder="admin@client.tl" />
                  </div>
                )}
              </>
            )}

            <DialogFooter className="sm:col-span-2 sticky bottom-0 -mx-6 -mb-4 px-6 py-3 bg-white border-t border-border mt-2">
              <Button type="button" variant="outline" onClick={() => setOpen(false)} data-testid="item-form-cancel">
                {t("cancel")}
              </Button>
              <Button type="submit" data-testid="item-form-save" className="bg-primary hover:bg-primary/90">
                {t("save")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmId} onOpenChange={(o) => !o && setConfirmId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("confirm_delete")}</AlertDialogTitle>
            <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="confirm-delete-item-cancel">{t("cancel")}</AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-delete-item-ok"
              onClick={() => deleteMut.mutate(confirmId)}
              className="bg-red-600 hover:bg-red-700"
            >
              {t("delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <ImportItemsDialog open={importOpen} onOpenChange={setImportOpen} />
      <RenewalQuotationDialog
        open={!!renewalItem}
        onOpenChange={(v) => !v && setRenewalItem(null)}
        item={renewalItem}
      />
    </div>
  );
}
